import { apiClient } from "./apiConfig";
import { SESSION_ID } from "./apiConfig";
import { detectPostType } from "../utils/postTypeUtils";

/**
 * API service for the newsfeed functionality
 */
export const newsfeedApi = {
	/**
	 * Fetch news items from the server
	 * @param {number} page - Page number
	 * @param {number} pageSize - Number of items per page
	 * @param {number} userId - ID of the current user
	 * @param {object} filters - Filters for the posts
	 * @returns {Promise} Promise with API response
	 */
	getPostItems: async (page, pageSize, userId, filters) => {
		const response = await apiClient.get(
			`/getposts/?page=${page}&pageSize=${pageSize}&activeUserId=${userId}&sort=${filters.sort}&contestid=${filters.contestid}`
		);

		if (filters.contestid === 0) {
			// For Glow Game, ensure Points field doesn't exist or is cleaned up
			if (response.data?.result?.items) {
				response.data.result.items = response.data.result.items.map((item) => {
					const cleanedItem = { ...item };
					// Debug: Log what Glow Game posts have
					if (item.Points !== undefined || item.Score !== undefined) {
						console.log("Glow Game post has Points/Score:", {
							Points: item.Points,
							Score: item.Score,
							Lumens: item.Lumens,
						});
					}
					// Remove Points field completely for Glow Game posts
					delete cleanedItem.Points;
					delete cleanedItem.Score;

					// IMPORTANT: Check ID fields FIRST before Type field
					// The API returns Type: "Compliment" for all posts, but grateful posts have GratitudeID
					// and proud posts have PrideID. We must prioritize these ID fields.
					if (cleanedItem.GratitudeID && cleanedItem.GratitudeID !== 0) {
						// Has GratitudeID - this is a grateful post
						cleanedItem.Type = "grateful";
						delete cleanedItem.PrideID;
					} else if (cleanedItem.PrideID && cleanedItem.PrideID !== 0) {
						// Has PrideID - this is a pride post
						cleanedItem.Type = "pride";
						delete cleanedItem.GratitudeID;
					} else if (cleanedItem.Type) {
						// No special ID fields, check the Type field
						const normalizedType =
							typeof cleanedItem.Type === "string"
								? cleanedItem.Type.toLowerCase()
								: cleanedItem.Type;
						if (
							normalizedType === "grateful" ||
							normalizedType === "gratitude"
						) {
							cleanedItem.Type = "grateful";
						} else if (
							normalizedType === "pride" ||
							normalizedType === "proud"
						) {
							cleanedItem.Type = "pride";
						} else {
							// "Compliment", "glow", or any other type defaults to glow
							cleanedItem.Type = "glow";
						}
						// Clear ID fields since we don't have them anyway
						delete cleanedItem.GratitudeID;
						delete cleanedItem.PrideID;
					} else {
						// No Type field and no ID fields - default to glow
						cleanedItem.Type = "glow";
						delete cleanedItem.GratitudeID;
						delete cleanedItem.PrideID;
					}

					return cleanedItem;
				});
			}
			return response.data;
		}
		const output = [];
		response.data.result.items.forEach((post) => {
			// Debug: Log what we're getting from API
			if (post.Points !== undefined || post.Score !== undefined) {
				console.log("Contest post data:", {
					ScoreID: post.ScoreID,
					Points: post.Points,
					Score: post.Score,
					Lumens: post.Lumens,
				});
			}
			// Normalize Type field if present
			// Note: "Compliment" from API means a glow (giving someone a compliment), NOT grateful
			let normalizedType = post.Type;

			if (normalizedType) {
				const typeLower =
					typeof normalizedType === "string"
						? normalizedType.toLowerCase()
						: normalizedType;
				if (typeLower === "grateful" || typeLower === "gratitude") {
					normalizedType = "grateful";
				} else if (typeLower === "pride" || typeLower === "proud") {
					normalizedType = "pride";
				} else if (typeLower === "glow" || typeLower === "compliment") {
					// "Compliment" is a glow action (giving a compliment to someone)
					normalizedType = "glow";
				}
				// If Type is unknown, leave it as-is (detectPostType will default to "glow")
			}

			const newPost = {
				GlowID: post.ScoreID,
				Lumens: post.Lumens,
				Score: post.Score !== undefined ? post.Score : post.Points, // Use Score or Points, don't default to 0
				Glow: post.Action,
				MemberID: post.MemberID,
				InputMethod: post.InputMethod,
				Type: normalizedType,
				Description: post.Description,
				Format: post.Format,
				QuestID: post.QuestID,
				ClientID: post.ClientID,
				CreatedAt: post.Datetime,
				MediaURL: post.MediaURL,
				LikesCount: post.LikesCount,
				CommentsCount: post.CommentsCount,
				SharesCount: post.SharesCount,
				Title: post.Title,
				ZipCode: post.Zipcode,
				UpdatedAt: post.UpdatedAt,
				IsLiked: post.IsLiked,
				AuthorName: post.AuthorName,
				AvatarURL: post.AvatarURL,
				ContestID: post.ContestID,
			};

			output.push(newPost);
		});

		const result = {
			items: output,
			total: response.data.result.total,
			hasMore: response.data.result.hasMore,
		};
		return { result };
	},

	/**
	 * Fetch a single post from the server
	 * @param {number} itemId - Post ID (GlowID, GratitudeID, or PrideID)
	 * @param {string} postType - Optional: The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} Promise with API response
	 */
	getPost: async (itemId, postType = null) => {
		const payload = {
			sessionid: SESSION_ID(),
		};

		// If postType is provided, use the specific ID field
		if (postType) {
			const idField =
				postType === "grateful"
					? "GratitudeID"
					: postType === "pride"
						? "GlowID"
						: "GlowID";

			payload.type = postType === "pride" ? "glow" : postType;
			payload[idField] = itemId;
		} else {
			// If no postType, let backend determine by trying all ID fields
			// Send all possible IDs and let backend find the right one
			payload.glowid = itemId;
			payload.gratitudeid = itemId;
			payload.prideid = itemId;
		}

		const response = await apiClient.post("/getpost/", payload);
		console.log("🔍 getPost API raw response:", response);
		return response;
	},

	/**
	 * Create a new post
	 * @param {FormData} formData - Form data containing post content and media
	 * @returns {Promise} Promise with API response
	 */
	createPost: async (formData) => {
		const response = await apiClient.post("/createpost/", formData);
		return response.data;
	},
	/**
	 * Gets a post graded (AI fills LumensAI and AIComment on the ToBeJudged record).
	 * @param {string|number} postID - The ID of the post to grade
	 * @param {boolean} autoJudge - If true, auto-approve and promote when LumensAI >= 1; if false, only fill AI suggestion for human judges
	 * @returns {Promise<Object>} - Response from the server
	 */
	gradeExistingPost: async (postID, autoJudge = true) => {
		const response = await apiClient.post("/gradeExistingPost/", {
			ID: postID,
			sessionid: SESSION_ID(),
			autoJudge: !!autoJudge,
		});
		return response.data;
	},

	/**
	 * Delete a post by its ID
	 * @param {string|number} glowid - The ID of the post to delete
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise<Object>} - Response from the server
	 */
	deletePost: async (postid, postType) => {
		return await apiClient.post("/deletepost/", {
			postid,
			type: postType,
			sessionid: SESSION_ID(), // TODO: remove this
		});
	},

	/**
	 * Hide a post for the current user
	 * @param {string|number} postId - The ID of the post to hide
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise<Object>} - Response from the server
	 */
	hidePost: async (postId, postType) => {
		try {
			return await apiClient.post(`/posts/${postId}/hide`, { type: postType });
		} catch (error) {
			console.error("Error hiding post:", error);
			throw error;
		}
	},

	/**
	 * Report a post
	 * @param {string|number} postId - The ID of the post to report
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @param {string} reason - The reason for reporting
	 * @returns {Promise<Object>} - Response from the server
	 */
	reportPost: async (postId, postType, reason) => {
		try {
			return await apiClient.post(`/posts/${postId}/report`, {
				type: postType,
				reason,
			});
		} catch (error) {
			console.error("Error reporting post:", error);
			throw error;
		}
	},

	/**
	 * Save/bookmark a post
	 * @param {string|number} postId - The ID of the post to save
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise<Object>} - Response from the server
	 */
	savePost: async (postId, postType) => {
		try {
			return await apiClient.post(`/posts/${postId}/save`, { type: postType });
		} catch (error) {
			console.error("Error saving post:", error);
			throw error;
		}
	},

	/**
	 * Fetch comments for an item
	 * @param {number} itemId - ID of the item (GlowID, GratitudeID, or PrideID)
	 * @param {number} activeUserID - ID of the active user
	 * @param {number} page - Page number
	 * @param {number} pageSize - Number of items per page
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} Promise with API response
	 */
	getComments: async (
		itemId,
		activeUserID,
		page = 1,
		pageSize = 5,
		postType = "glow"
	) => {
		const response = await apiClient.post("/getcomments/", {
			type: postType,
			itemId,
			page,
			pageSize,
			activeUserID,
		});
		return response.data;
	},

	/**
	 * Fetch comments for an item
	 * @param {number} itemId - ID of the item
	 * @returns {Promise} Promise with API response
	 */
	getReplies: async (commentID, activeUserID, page = 1, pageSize = 5) => {
		const response = await apiClient.post("/getreplies/", {
			commentID,
			page,
			pageSize,
			activeUserID,
		});
		return response.data;
	},

	/**
	 * Like or unlike a glow
	 * @param {number} itemId - ID of the post (GlowID, GratitudeID, or PrideID)
	 * @param {number} MemberID - ID of the user
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} Promise with API response
	 */
	likeGlow: async (itemId, MemberID, postType) => {
		// Determine the correct ID field name based on post type
		const idField =
			postType === "grateful"
				? "GratitudeID"
				: postType === "pride"
					? "GlowID"
					: "GlowID";

		const payload = {
			[idField]: itemId, // Dynamic field name based on post type
			MemberID,
			type: postType === "pride" ? "glow" : postType,
		};

		return await apiClient.post("/likeglow/", payload);
	},

	/**
	 * Share a post
	 * @param {number} postID - ID of the post (GlowID, GratitudeID, or PrideID)
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} Promise with API response
	 */
	sharePost: async (postID, postType) => {
		// Determine the correct ID field name based on post type
		const idField =
			postType === "grateful"
				? "GratitudeID"
				: postType === "pride"
					? "GlowID"
					: "GlowID";

		const payload = {
			[idField]: postID, // Dynamic field name based on post type
			type: postType === "pride" ? "glow" : postType,
		};

		return await apiClient.post("/sharepost/", payload);
	},

	/**
	 * Like or unlike a comment
	 * @param {number} commentId - ID of the comment
	 * @param {string} memberId - ID of the member
	 * @returns {Promise} Promise with API response
	 */
	likeComment: async (commentID, memberID) => {
		return await apiClient.post("/likecomment/", {
			commentID,
			memberID,
		});
	},

	/**
	 * Pin or unpin a comment
	 * @param {number} itemId - Post ID (GlowID, GratitudeID, or PrideID)
	 * @param {number} commentId - Comment ID
	 * @param {number} memberid - Member ID
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} - Axios response
	 */
	pinComment: (itemId, commentId, memberid, postType) => {
		// Determine the correct ID field name based on post type
		const idField =
			postType === "grateful"
				? "GratitudeID"
				: postType === "pride"
					? "GlowID"
					: "GlowID";

		const payload = {
			[idField]: itemId, // Dynamic field name based on post type
			CommentID: commentId,
			MemberID: memberid,
			type: postType === "pride" ? "glow" : postType,
		};

		return apiClient.post("/pincomment/", payload);
	},

	/**
	 * Add a comment to an item
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @param {number} itemId - ID of the item (will be sent as GlowID, GratitudeID, or PrideID)
	 * @param {string} comment - Comment text
	 * @returns {Promise} Promise with API response
	 */
	addComment: async (
		postType,
		itemId,
		Comment,
		MemberID,
		TargetReplyID = null,
		ParentID = null,
		ClientID = 0,
		ContestID = 0,
		Format = null,
		CommentType = "",
		MediaURL = ""
	) => {
		const DateTime = new Date().toISOString();

		// Determine the correct ID field name based on post type
		const idField =
			postType === "grateful"
				? "GratitudeID"
				: postType === "pride"
					? "GlowID"
					: "GlowID";

		const payload = {
			type: postType === "pride" ? "glow" : postType,
			[idField]: itemId, // Dynamic field name based on post type
			Comment,
			MediaURL,
			MemberID,
			TargetReplyID,
			ParentID,
			ClientID,
			ContestID,
			Format,
			CommentType,
			DateTime,
		};

		return await apiClient.post("/addcomment/", payload);
	},

	/**
	 * Retrieve the active event for a contest
	 * @param {number} contestID - Contest ID
	 * @returns {Promise} Promise with API response
	 */
	getActiveEventForContest: async (contestID) => {
		return await apiClient.get(`/getactiveevent/?contestID=${contestID}`);
	},

	/**
	 * Send lumens to a post
	 * @param {number} memberID - ID of the member sending lumens
	 * @param {number} lumens - Amount of lumens to send
	 * @param {number} itemId - ID of the post (GlowID, GratitudeID, or PrideID)
	 * @param {string} postType - The type of the post (e.g., 'glow', 'grateful', 'pride')
	 * @returns {Promise} Promise with API response
	 */
	sendLumens: async (memberID, lumens, itemId, postType) => {
		// Determine the correct ID field name based on post type
		const idField =
			postType === "grateful"
				? "GratitudeID"
				: postType === "pride"
					? "GlowID"
					: "GlowID";

		const payload = {
			type: postType === "pride" ? "glow" : postType,
			memberID,
			lumens,
			[idField]: itemId, // Dynamic field name based on post type
		};

		return await apiClient.post(`/sendlumens/`, payload);
	},

	/**
	 * Fetch the most recent glows for a user
	 * @param {number} memberID - ID of the user
	 * @param {number} count - Number of glows to fetch
	 * @returns {Promise} Promise with API response
	 */
	getMostRecentGlowsForUser: async (memberID, count = 10) => {
		return await apiClient.get(`/getmostrecentglowsforuser/`, {
			params: {
				memberid: memberID,
				count,
			},
		});
	},

	getProudPostOptions: async (memberID) => {
		return await apiClient.get(`/findproudsfortheday/?memberID=${memberID}`);
	},

	/**
	 * Fetch posts created by a specific user (paginated)
	 * @param {number} page - Page number
	 * @param {number} pageSize - Number of items per page
	 * @param {number} memberID - ID of the user whose posts to fetch
	 * @returns {Promise} Promise with API response
	 */
	getMyPosts: async (page, pageSize, memberID) => {
		const response = await apiClient.get(
			`/getposts/?page=${page}&pageSize=${pageSize}&activeUserId=${memberID}&myPostsOnly=true`
		);

		// Transform response to match expected format
		if (response.data?.result?.items) {
			response.data.result.items = response.data.result.items.map((item) => {
				// First, check if ID fields exist and are non-zero (most reliable)
				let postType = "glow"; // default

				// Check for existing ID fields first (must be truthy, not 0)
				if (item.PrideID && item.PrideID !== 0) {
					postType = "pride";
					item.Type = "pride";
				} else if (item.GratitudeID && item.GratitudeID !== 0) {
					postType = "grateful";
					item.Type = "grateful";
				} else if (item.Type !== undefined && item.Type !== null) {
					// Normalize Type to lowercase first (API returns "Pride", "Grateful", "Compliment", etc.)
					const normalizedType =
						typeof item.Type === "string" ? item.Type.toLowerCase() : item.Type;

					// Map Type to post type, handling variations
					// Note: "Compliment" from API means a glow (giving someone a compliment), NOT grateful
					if (normalizedType === "grateful" || normalizedType === "gratitude") {
						item.Type = "grateful";
					} else if (normalizedType === "pride" || normalizedType === "proud") {
						item.Type = "pride";
					} else if (
						normalizedType === "glow" ||
						normalizedType === "compliment"
					) {
						// "Compliment" is a glow action (giving a compliment to someone)
						item.Type = "glow";
					} else {
						// Unknown type - default to glow
						item.Type = "glow";
					}
				} else {
					// No Type or ID fields - default to glow
					item.Type = "glow";
				}

				return item;
			});
		}

		return response.data;
	},
};

// Development fallback implementation
if (import.meta.env.DEV) {
	// Mock implementation for media uploads
	apiClient.interceptors.response.use(
		(response) => response,
		(error) => {
			if (
				error.config.url === "/create-post" &&
				error.config.headers["Content-Type"]?.includes("multipart/form-data")
			) {
				// Extract media type from FormData
				const formData = error.config.data;
				let mediaType = "text";
				let mediaUrl = null;
				let content = "";

				try {
					if (formData instanceof FormData) {
						const media = formData.get("media");
						content = formData.get("content") || "";

						if (media) {
							// Determine media type
							if (media.type.startsWith("audio/")) {
								mediaType = "audio";
								mediaUrl = "/demo/sample-audio.mp3";
							} else if (media.type.startsWith("video/")) {
								mediaType = "video";
								mediaUrl = "/demo/sample-video.mp4";
							} else if (media.type.startsWith("image/")) {
								mediaType = "image";
								mediaUrl = "/demo/sample-image.jpg";
							}
						}
					}
				} catch (e) {
					console.warn("Could not extract media from FormData", e);
				}

				// Mock successful response
				return Promise.resolve({
					data: {
						success: true,
						message: "Post created successfully",
						post: {
							id: Math.floor(Math.random() * 10000),
							title: content ? content.substring(0, 50) : "New media post",
							author: "Alex Wilson",
							content,
							date: new Date().toISOString(),
							likes: 0,
							comments: 0,
							shares: 0,
							avatar: "https://randomuser.me/api/portraits/men/3.jpg",
							medialink: mediaUrl,
							mediatype: mediaType,
							isLiked: false,
						},
					},
				});
			}

			return Promise.reject(error);
		}
	);
}
